import { type QueryClient, useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';

/**
 * 367 (FR-001, FR-002): one role on the Roles page, as the platform answers for this viewer. `kind: 'organisation'`
 * is given with a supplier; `reason` says why a role the viewer may not give is refused.
 */
export interface RoleOption {
  name: string;
  kind: 'platform' | 'organisation';
  grantable: boolean;
  reason: 'staff_role_admin_only' | 'superadmin_role_only' | 'role_not_grantable' | null;
}

export interface RoleOptions {
  /** 367: every role, each with whether the viewer may give it. */
  roleOptions: RoleOption[];
  /** The platform roles this person may give (334's list). */
  grantable: string[];
  /** Every role's name: the users-list filter. */
  all: string[];
}

/** The query key, so the Roles pages can refresh the role choices when a role is created or edited (367 R8). */
export const ROLE_OPTIONS_KEY = ['users', 'role-options'] as const;

/**
 * 367 (FR-003): after a role is created or its permissions change, the role choices are stale everywhere they are
 * shown: the create-user and bulk lists (`ROLE_OPTIONS_KEY`) and every user page (`['user', id]`, which carries
 * `role_options`).
 */
export function refreshRoleChoices(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ROLE_OPTIONS_KEY });
  queryClient.invalidateQueries({ queryKey: ['user'] });
}

/**
 * What an older backend, without `GET /users/role-options`, used to offer. Display defaults only: the platform still
 * decides every grant, and refuses one the viewer may not give. (367: no Supplier, a retired role.)
 */
export const FALLBACK_ROLE_OPTIONS: RoleOptions = {
  roleOptions: [
    { name: 'Admin', kind: 'platform', grantable: true, reason: null },
    { name: 'Customer', kind: 'platform', grantable: true, reason: null },
  ],
  grantable: ['Customer', 'Admin'],
  all: ['Admin', 'Host', 'Customer'],
};

/**
 * 334 (#539, FR-003, FR-004) and 367: the role choices come from the platform, which applies 333's cover rule, 334's
 * "staff roles by administrators only" and 367's "only a SuperAdmin gives SuperAdmin".
 */
export function useRoleOptions(): RoleOptions {
  const { data } = useQuery({
    queryKey: ROLE_OPTIONS_KEY,
    queryFn: () =>
      api.get<{ role_options?: RoleOption[]; grantable?: string[]; all?: string[] }>(
        '/users/role-options',
      ),
    staleTime: 60_000,
    retry: false,
  });
  const grantable = Array.isArray(data?.grantable)
    ? data.grantable
    : FALLBACK_ROLE_OPTIONS.grantable;
  return {
    // An older backend without `role_options`: its `grantable` list, as givable platform roles.
    roleOptions: Array.isArray(data?.role_options)
      ? data.role_options
      : grantable.map((name) => ({ name, kind: 'platform', grantable: true, reason: null })),
    grantable,
    all: Array.isArray(data?.all) ? data.all : FALLBACK_ROLE_OPTIONS.all,
  };
}

/**
 * 367 (FR-011, design 1g): the organisation roles on the Roles page, Host first, for the membership role choices.
 * When the platform cannot say (no `manage User`, or an older backend), Host alone: the platform still decides.
 */
export function useOrganisationRoleNames(): string[] {
  const { roleOptions } = useRoleOptions();
  const names = roleOptions
    .filter((o) => o.kind === 'organisation')
    .map((o) => o.name)
    .sort((a, b) => (a === 'Host' ? -1 : b === 'Host' ? 1 : a.localeCompare(b)));
  return names.length > 0 ? names : ['Host'];
}
